import { useMemo } from "react";
import { useQuery } from "@apollo/client";
import { WAVE_CLOSE_ATTEMPTS } from "../queries/waves";
import { formatDateSGT, formatTimeSGT } from "./dateFormat";

const money = (v) => {
    if (v == null) return "-";
    const n = Number(v);
    if (!Number.isFinite(n)) return "-";
    const sign = n >= 0 ? "" : "-";
    return `${sign}$${Math.abs(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

// Stable reference so the useMemo below doesn't re-run on every render while loading.
const EMPTY = [];

// Group rows into baseline (Phase 2) and each Phase 3 arm, and compute the
// abandonment rate, the headline research measure for the obstruction study.
function buildSummary(rows) {
    const buckets = {};
    const add = (key) => { if (!buckets[key]) buckets[key] = { total: 0, abandoned: 0 }; return buckets[key]; };
    for (const r of rows) {
        const label = r.phase === 2 ? "Phase 2 (baseline)" : `Phase 3, Arm ${r.intervention_arm || "?"}`;
        const b = add(label);
        b.total += 1;
        if (r.abandonment_flag) b.abandoned += 1;
    }
    // Stable ordering: baseline first, then arms A/B/C.
    const order = ["Phase 2 (baseline)", "Phase 3, Arm A", "Phase 3, Arm B", "Phase 3, Arm C"];
    return Object.entries(buckets).sort(
        ([a], [b]) => (order.indexOf(a) - order.indexOf(b)) || a.localeCompare(b)
    );
}

export default function CloseAttemptsTab({ waveId }) {
    const { loading, data, error } = useQuery(WAVE_CLOSE_ATTEMPTS, {
        variables: { waveId }, fetchPolicy: "cache-and-network",
    });

    const rows = data?.waveCloseAttempts ?? EMPTY;
    const summary = useMemo(() => buildSummary(rows), [rows]);

    return (
        <div className="admin-section">
            <h3>Close attempts: abandonment (Wave E)</h3>
            <p style={{ color: "#666", fontSize: 13 }}>
                Phase 2 rows are the pre-intervention baseline (no session, no arm). Compare their
                abandonment rate against Phase 3 rows grouped by intervention arm.
            </p>

            {loading && <p>Loading…</p>}
            {error && <p style={{ color: "crimson" }}>{error.message}</p>}

            {!loading && !error && (
                <>
                    {/* Abandonment-rate summary — cards grow equally to fill the row width */}
                    <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 20 }}>
                        {summary.length === 0 && <span style={{ color: "#888" }}>No close attempts yet.</span>}
                        {summary.map(([label, { total, abandoned }]) => {
                            const pct = total ? Math.round((abandoned / total) * 100) : 0;
                            return (
                                <div key={label} style={{
                                    flex: "1 1 0", minWidth: 170,
                                    border: "1px solid #cbd5e1", borderRadius: 6, padding: "16px 20px",
                                    display: "flex", flexDirection: "column", gap: 8,
                                }}>
                                    <div style={{ fontSize: 12, color: "#475569" }}>{label}</div>
                                    <div style={{ fontSize: 22, fontWeight: 700 }}>{pct}%</div>
                                    <div style={{ fontSize: 12, color: "#64748b" }}>
                                        {abandoned} abandoned / {total} attempts
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/* Detail table */}
                    <table className="admin-table">
                        <thead>
                            <tr>
                                <th>Attempt</th><th>Participant</th><th>Phase</th><th>Arm</th>
                                <th>Session</th><th>Outcome</th><th>P&L at abandon</th><th>P&L at close</th>
                                <th>Initiated Date</th><th>Initiated Time</th>
                                <th>Finalised Date</th><th>Finalised Time</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.length === 0 && (
                                <tr><td colSpan={12} style={{ color: "#888", textAlign: "center" }}>No close attempts yet.</td></tr>
                            )}
                            {rows.map((r) => {
                                const finalisedAt = r.abandoned_at || r.executed_at;
                                return (
                                    <tr key={r.attempt_id}>
                                        <td>{r.attempt_id}</td>
                                        <td>{r.participant_id}</td>
                                        <td>{r.phase}</td>
                                        <td>{r.intervention_arm || "-"}</td>
                                        <td>{r.session_id || "-"}</td>
                                        <td>
                                            {r.abandonment_flag
                                                ? <span style={{ color: "#b45309", fontWeight: 600 }}>Abandoned</span>
                                                : r.executed_at
                                                    ? <span style={{ color: "#16a34a", fontWeight: 600 }}>Completed</span>
                                                    : <span style={{ color: "#64748b" }}>In progress</span>}
                                        </td>
                                        <td>{money(r.pnl_at_abandonment)}</td>
                                        <td>{money(r.pnl_at_close)}</td>
                                        <td>{formatDateSGT(r.initiated_at)}</td>
                                        <td>{formatTimeSGT(r.initiated_at)}</td>
                                        <td>{finalisedAt ? formatDateSGT(finalisedAt) : "-"}</td>
                                        <td>{finalisedAt ? formatTimeSGT(finalisedAt) : "-"}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </>
            )}
        </div>
    );
}
